import React, { useEffect } from 'react'
import { AnimalInsightsDtoType, AnimalSearchItemDto } from '../../interactors/gen/backendClient'
import { Avatar, AvatarGroup, Box, Card, CardContent, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import { getAnimalProfilePhotoSrc } from '../../utils/S3-links'
import { getEmojiSpecies, getReadableIdentificationNumber } from '../../domain/Animal/AnimalDisplay'
import {
  // AnimalInsightsAvatarGroup,
  AnimalSexIcon,
  AnimalStatusChip,
  backgroundShouldBeGrey,
  CircleWithNumber,
  HtmlTooltip,
} from './AnimalsTableItem'
import { fullName } from '../../domain/Account/AccountDisplay'
import { InfoBox } from '../common/InfoBox'
import { colors, lightGrey } from '../theme'
import { setOpacity } from '../../utils/setOpacity'
import dayjs from 'dayjs'

type CardProps = {
  animal: AnimalSearchItemDto
  myAnimalsSection?: boolean
  assignAnimalId?: React.Dispatch<React.SetStateAction<string | null>>
  selectedAnimalId?: string
  index: number
  expandedCardIndex: number | null
  setExpandedCardIndex: React.Dispatch<React.SetStateAction<number | null>>
  openedTooltip: boolean
  setOpenedTooltip: React.Dispatch<React.SetStateAction<boolean>>
}
const AnimalsListCard: React.FC<CardProps> = ({
  animal,
  myAnimalsSection = false,
  assignAnimalId,
  selectedAnimalId,
  index,
  expandedCardIndex,
  setExpandedCardIndex,
  openedTooltip,
  setOpenedTooltip,
}) => {
  const navigate = useNavigate()
  const [open, setOpen] = React.useState(false)

  const handleClick = () => {
    if (assignAnimalId) {
      assignAnimalId(animal.id)
      return
    }
    return navigate(`/animaux/${animal.id}`)
  }
  const handleOpen = (event: React.SyntheticEvent<Element, Event>) => {
    event.stopPropagation()
    setOpen(true)
    setExpandedCardIndex(index)
    setOpenedTooltip(true)
  }
  const handleClose = (event: Event | React.SyntheticEvent<Element, Event>) => {
    event.stopPropagation()
    setOpen(false)
    setExpandedCardIndex(index)
    setOpenedTooltip(false)
  }

  useEffect(() => {
    if (index !== expandedCardIndex) {
      setOpen(false)
    }
  }, [expandedCardIndex])

  const profileImageKey = animal.images?.profileImageKey
  const { responsibilities } = animal
  const order: Array<AnimalInsightsDtoType> = ['info', 'error', 'warning']

  return (
    <Card
      onClick={
        openedTooltip
          ? (event) => {
              handleClose(event)
            }
          : handleClick
      }
      sx={{
        borderRadius: 4,
        border: '1px solid #E4E8F2',
        backgroundColor:
          selectedAnimalId === animal.id
            ? setOpacity(colors.bermudaGray, 0.35)
            : backgroundShouldBeGrey(animal, myAnimalsSection)
            ? lightGrey
            : '',
        boxShadow: 'none',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          // Keep a small bottom padding; MUI spacing unit makes 16 => 128px which is too large on mobile.
          '&:last-child': { paddingBottom: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box display="flex" gap={1.5} alignItems="flex-start">
          {/* Avatar */}
          <Avatar
            alt={animal.name}
            src={profileImageKey ? getAnimalProfilePhotoSrc(profileImageKey) : ''}
            sx={{
              height: 56,
              width: 56,
            }}
          />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.15 }} noWrap>
                {animal.name}
              </Typography>
              <Typography sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                {getEmojiSpecies(animal.species)}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>
                {animal.sex && <AnimalSexIcon sex={animal.sex} />}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.6 }}>
              <AnimalStatusChip
                animal={animal}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  borderRadius: '999px',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            </Box>
            {animal.identificationNumber && (
              <Typography
                sx={{ mt: 0.6, color: '#7A8197', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}
                noWrap
              >
                {getReadableIdentificationNumber(animal.identificationNumber?.toString())}
              </Typography>
            )}
            {animal.birthdate && (
              <Typography sx={{ fontWeight: 800, color: colors.cello, fontSize: '0.85rem' }} noWrap>
                {`${animal.sex === 'female' ? 'Née' : 'Né'} le ${dayjs(animal.birthdate).format('DD/MM/YYYY')}`}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75, flexShrink: 0 }}>
            <Box
              onClick={(event) => {
                event.stopPropagation()
              }}
            >
              <HtmlTooltip
                sx={{
                  paddingX: `calc((100vw - 300px)/2)`,
                }}
                title={
                  <React.Fragment>
                    <Box display="flex" justifyContent="flex-end">
                      <IconButton
                        sx={{
                          paddingY: 0,
                        }}
                        onClick={handleClose}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    {animal.insights
                      .sort((a, b) => {
                        const typeA = a.type
                        const typeB = b.type
                        const indexA = order.indexOf(typeA)
                        const indexB = order.indexOf(typeB)
                        return indexA - indexB
                      })
                      .map((insight, idx) => (
                        <InfoBox
                          key={idx}
                          messageType={insight.type}
                          content={insight.message}
                          sx={{ marginBottom: 1 }}
                        />
                      ))}
                  </React.Fragment>
                }
                open={open}
              >
                <AvatarGroup onClick={open ? handleClose : handleOpen}>
                  {animal.insightsCounters.info !== 0 && (
                    <CircleWithNumber messageType="info" number={animal.insightsCounters.info} />
                  )}
                  {animal.insightsCounters.warning !== 0 && (
                    <CircleWithNumber messageType="warning" number={animal.insightsCounters.warning} />
                  )}
                  {animal.insightsCounters.error !== 0 && (
                    <CircleWithNumber messageType="error" number={animal.insightsCounters.error} />
                  )}
                </AvatarGroup>
              </HtmlTooltip>
            </Box>
          </Box>
        </Box>

        {/* Box 3 : Member in charge, host family, cage number*/}
        <Box paddingBottom={0.5} fontSize="15px" sx={{ mt: 0.25 }}>
          {animal.sanitarySurveillanceUntil && (
            <Box sx={{ mb: 0.75 }}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 20,
                  px: 1.25,
                  borderRadius: 999,
                  backgroundColor: '#698DAC',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                }}
              >
                {`Surveillance jusqu'au ${dayjs(animal.sanitarySurveillanceUntil).format('DD/MM/YYYY')}`}
              </Box>
            </Box>
          )}
          <Typography sx={{ fontSize: '0.85rem', fontStyle: 'italic', color: colors.cello, mt: 0.25 }}>
            {responsibilities?.memberInChargeAccount &&
              `Référent : ${fullName(responsibilities?.memberInChargeAccount)}`}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', fontStyle: 'italic', color: colors.cello }}>
            {responsibilities?.hostFamilyInChargeAccount
              ? `FA : ${fullName(responsibilities?.hostFamilyInChargeAccount)}`
              : animal.responsibilities?.cageNumber && `Box N°${animal.responsibilities?.cageNumber}`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default AnimalsListCard
